import React, { useState } from "react";

// --- Helpers ---
const formatDateTime = (timestamp) => {
  if (!timestamp) return "N/A";
  try {
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "Invalid Date";
  }
};

const formatTime = (timestamp) => {
  if (!timestamp) return "N/A";
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "Invalid Time";
  }
};

// --- Date Picker ---
const DatePicker = ({ onApply, onClear, currentRange }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const handleApply = () => {
    if (fromDate && toDate) {
      onApply({ fromDate, toDate });
      setShowPicker(false);
    }
  };

  const handleClear = () => {
    setFromDate("");
    setToDate("");
    onClear();
    setShowPicker(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowPicker(!showPicker)}
        className={`btn border shadow-card transition-colors ${
          currentRange
            ? "border-brand-300 bg-brand-50 text-brand-700 hover:bg-brand-100/70"
            : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
        }`}
      >
        <svg className="h-4 w-4 text-current opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        Date Range
        {currentRange && (
          <span className="ml-0.5 rounded-full bg-brand-600 px-1.5 py-px text-[11px] font-semibold text-white">1</span>
        )}
      </button>

      {showPicker && (
        <div className="absolute top-full left-0 z-20 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-neutral-200 bg-white p-4 shadow-xl">
          <div className="space-y-3">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-neutral-600">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="input w-full"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-neutral-600">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="input w-full"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleApply}
                disabled={!fromDate || !toDate}
                className="btn-primary flex-1"
              >
                Apply
              </button>
              <button
                onClick={handleClear}
                className="btn-ghost"
              >
                Clear
              </button>
              <button
                onClick={() => setShowPicker(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Station Filter ---
const StationFilter = ({ stations, selectedStations, onToggle, onClear }) => {
  const [showFilter, setShowFilter] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setShowFilter(!showFilter)}
        className={`btn border shadow-card transition-colors ${
          selectedStations.length > 0
            ? "border-brand-300 bg-brand-50 text-brand-700 hover:bg-brand-100/70"
            : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
        }`}
      >
        <svg className="h-4 w-4 text-current opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
        Stations
        {selectedStations.length > 0 && (
          <span className="ml-0.5 rounded-full bg-brand-600 px-1.5 py-px text-[11px] font-semibold tabular-nums text-white">
            {selectedStations.length}
          </span>
        )}
      </button>

      {showFilter && (
        <div className="absolute top-full left-0 z-20 mt-2 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-neutral-200 px-3 py-2.5">
            <h4 className="text-13 font-semibold text-brand-950">Filter by Station</h4>
            {selectedStations.length > 0 && (
              <button
                onClick={onClear}
                className="text-xs font-medium text-brand-600 hover:text-brand-700"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="p-2 max-h-64 overflow-y-auto">
            {stations.length === 0 ? (
              <div className="px-3 py-6 text-center text-13 text-neutral-500">
                No stations available
              </div>
            ) : (
              <div className="space-y-0.5">
                {stations.map((station) => (
                  <label
                    key={station}
                    className="group flex cursor-pointer items-center rounded-md px-2.5 py-1.5 transition-colors hover:bg-neutral-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStations.includes(station)}
                      onChange={() => onToggle(station)}
                      className="h-4 w-4 rounded border-neutral-300 accent-brand-600"
                    />
                    <span className="ml-2.5 flex-1 text-13 text-neutral-700 group-hover:text-brand-950">
                      {station}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="border-t border-neutral-200 bg-neutral-50 p-2.5">
            <button
              onClick={() => setShowFilter(false)}
              className="btn-secondary w-full"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Main Filters Component ---
const Filters = ({ 
  customRange, 
  selectedStations, 
  uniqueStations, 
  onCustomRangeChange, 
  onSelectedStationsChange,
  activeFiltersCount 
}) => {
  const handleStationToggle = (stationId) => {
    onSelectedStationsChange(
      selectedStations.includes(stationId)
        ? selectedStations.filter((id) => id !== stationId)
        : [...selectedStations, stationId]
    );
  };

  const handleClearAll = () => {
    onCustomRangeChange(null);
    onSelectedStationsChange([]);
  };

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-xs font-medium uppercase tracking-[0.06em] text-neutral-500">Filters</span>
        <DatePicker
          onApply={onCustomRangeChange}
          onClear={() => onCustomRangeChange(null)}
          currentRange={customRange}
        />
        <StationFilter
          stations={uniqueStations}
          selectedStations={selectedStations}
          onToggle={handleStationToggle}
          onClear={() => onSelectedStationsChange([])}
        />
        {activeFiltersCount > 0 && (
          <button
            onClick={handleClearAll}
            className="ml-auto text-13 font-medium text-brand-600 hover:text-brand-700"
          >
            Clear all filters
          </button>
        )}
      </div>

      {/* Active filters display */}
      {(customRange || selectedStations.length > 0) && (
        <div className="mt-3 border-t border-neutral-100 pt-3">
          <div className="flex flex-wrap gap-2">
            {customRange && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 ring-1 ring-inset ring-brand-100">
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {customRange.fromDate} to {customRange.toDate}
                <button
                  onClick={() => onCustomRangeChange(null)}
                  className="-mr-1 rounded-full p-0.5 hover:bg-brand-100"
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </span>
            )}
            {selectedStations.map((station) => (
              <span
                key={station}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 ring-1 ring-inset ring-brand-100"
              >
                {station}
                <button
                  onClick={() => handleStationToggle(station)}
                  className="-mr-1 rounded-full p-0.5 hover:bg-brand-100"
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Filters;